import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  ConfirmDialog,
  DataTable,
  Field,
  KeyValue,
  Skeleton,
  TextInput,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime, formatNumber } from '../lib/format';

interface Settings {
  emergencyStopAll: boolean;
  disabledProviderAccountIds: string[];
  disabledOperationKeys: string[];
  disabledClientAppIds: string[];
  globalRpmLimit: number | null;
  globalConcurrentLimit: number | null;
  telemetryRetentionDays: number;
  payloadLoggingEnabled: boolean;
  internalHostAllowlist: string[];
  hubDisplayName: string;
  hubInstanceId: string;
  developerTokenDefaultTtlDays: number;
  twoFactorEmailEnabled: boolean;
  catalogVersion: number;
  runtime: {
    publicBaseUrl: string;
    mcpUrl: string;
    environmentMode: string;
    redisDistributed: boolean;
    internalNetworkEnabled: boolean;
    pgvectorEnabled: boolean;
    toolListChangedEnabled: boolean;
    encryptionKeyVersions: string[];
    primaryEncryptionKeyVersion: string;
    telemetryRetentionDays: number;
  };
}

export function SettingsPage({ role }: { role: string }) {
  const settings = useResource<Settings>('/api/admin/settings');
  const retention = useResource<{
    telemetryRetentionDays: number;
    cutoff: string;
    pendingCleanup: Record<string, number>;
  }>('/api/admin/retention');
  const toast = useToast();
  const isOwner = role === 'OWNER';

  const [stopConfirm, setStopConfirm] = useState(false);

  if (settings.loading || !settings.data) return <Skeleton rows={8} />;
  const data = settings.data;

  const emergency = async (action: string, targetId?: string) => {
    try {
      await api.post('/api/admin/emergency', {
        action,
        ...(targetId ? { targetId } : {}),
        reason: 'Z panelu administracyjnego',
      });
      toast.success('Przełącznik awaryjny zaktualizowany');
      settings.reload();
    } catch (error) {
      const described = describeError(error);
      toast.error(described.title, described.body);
    }
  };

  return (
    <>
      <PageHeader
        title="Ustawienia"
        description="Konfiguracja globalna, retencja i przełączniki awaryjne."
      />

      {data.emergencyStopAll && (
        <Callout tone="danger">
          <strong>Hub jest zatrzymany.</strong> Wszystkie wywołania zwracają EMERGENCY_STOP. Wznów
          pracę poniżej.
        </Callout>
      )}

      <Card
        title="Środowisko uruchomieniowe"
        description="Wartości pochodzą ze zmiennych środowiskowych i nie są edytowalne z panelu."
      >
        <KeyValue
          items={[
            ['Adres publiczny', <span className="ah-mono">{data.runtime.publicBaseUrl}</span>],
            ['Endpoint MCP', <span className="ah-mono">{data.runtime.mcpUrl}</span>],
            [
              'Tryb',
              <Badge tone={data.runtime.environmentMode === 'production' ? 'danger' : 'neutral'}>
                {data.runtime.environmentMode}
              </Badge>,
            ],
            [
              'Redis',
              <Badge tone={data.runtime.redisDistributed ? 'ok' : 'warn'}>
                {data.runtime.redisDistributed ? 'rozproszony' : 'fallback w procesie'}
              </Badge>,
            ],
            [
              'Sieć wewnętrzna',
              <Badge tone={data.runtime.internalNetworkEnabled ? 'warn' : 'ok'}>
                {data.runtime.internalNetworkEnabled ? 'włączona' : 'wyłączona'}
              </Badge>,
            ],
            [
              'Klucze szyfrowania',
              <span className="ah-mono">
                {data.runtime.encryptionKeyVersions.join(', ')} (główny:{' '}
                {data.runtime.primaryEncryptionKeyVersion})
              </span>,
            ],
            [
              'pgvector',
              data.runtime.pgvectorEnabled
                ? 'włączony'
                : 'wyłączony (wyszukiwanie pełnotekstowe działa zawsze)',
            ],
            [
              'Powiadomienia o zmianie listy narzędzi',
              data.runtime.toolListChangedEnabled ? 'włączone' : 'wyłączone',
            ],
            ['Identyfikator instancji', <span className="ah-mono">{data.hubInstanceId}</span>],
            ['Wersja katalogu', formatNumber(data.catalogVersion)],
          ]}
        />
        <Callout tone="warn">
          Utrata klucza <code className="ah-mono">APP_ENCRYPTION_KEYS</code> oznacza bezpowrotną
          utratę wszystkich zapisanych poświadczeń dostawców. Przechowuj go poza bazą danych i twórz
          jego kopię zapasową osobno.
        </Callout>
      </Card>

      {/* Keyed on the loaded values so the form initializes from data without
          synchronising state inside an effect. */}
      <GlobalSettingsForm
        key={`${data.hubDisplayName}:${data.telemetryRetentionDays}:${data.globalRpmLimit}:${data.globalConcurrentLimit}:${data.developerTokenDefaultTtlDays}`}
        settings={data}
        isOwner={isOwner}
        onSaved={() => settings.reload()}
      />

      <Card
        title="Retencja telemetrii"
        description="Zadanie w tle usuwa dane starsze niż skonfigurowany próg."
      >
        {retention.data && (
          <KeyValue
            items={[
              ['Próg', formatDateTime(retention.data.cutoff)],
              ['Żądania do usunięcia', formatNumber(retention.data.pendingCleanup.requests ?? 0)],
              ['Próby do usunięcia', formatNumber(retention.data.pendingCleanup.attempts ?? 0)],
              ['Rekordy zużycia', formatNumber(retention.data.pendingCleanup.usage ?? 0)],
              ['Wygasłe pakiety', formatNumber(retention.data.pendingCleanup.expiredPacks ?? 0)],
            ]}
          />
        )}
        <Callout>
          Treści żądań i odpowiedzi nie są przechowywane w ogóle — retencja dotyczy wyłącznie
          metadanych i liczników.
        </Callout>
      </Card>

      <Card
        title="Drugi składnik logowania"
        description="Kod jednorazowy wysyłany e-mailem. Wymaga skonfigurowanej poczty (SMTP_URL i MAIL_FROM) — bez niej włączenie zostanie odrzucone."
      >
        <p style={{ fontSize: 13, marginBottom: 12 }}>
          Stan: <strong>{data.twoFactorEmailEnabled ? 'włączony' : 'wyłączony'}</strong>. Sam ten
          przełącznik nie wystarczy — drugi składnik działa dopiero wtedy, gdy jest włączony także
          na koncie użytkownika.
        </p>
        {isOwner && (
          <Button
            variant={data.twoFactorEmailEnabled ? 'danger' : 'primary'}
            onClick={async () => {
              try {
                await api.patch('/api/admin/settings', {
                  twoFactorEmailEnabled: !data.twoFactorEmailEnabled,
                });
                toast.success('Ustawienie zapisane');
                settings.reload();
              } catch (error) {
                const described = describeError(error);
                toast.error(described.title, described.body);
              }
            }}
          >
            {data.twoFactorEmailEnabled ? 'Wyłącz drugi składnik' : 'Włącz drugi składnik'}
          </Button>
        )}
      </Card>

      {isOwner && (
        <Card
          title="Przełączniki awaryjne"
          description="Zapisywane w PostgreSQL, więc przetrwają restart i wyczyszczenie cache."
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {data.emergencyStopAll ? (
              <Button variant="primary" onClick={() => emergency('RESUME_ALL')}>
                Wznów cały ruch
              </Button>
            ) : (
              <Button variant="danger" onClick={() => setStopConfirm(true)}>
                Zatrzymaj wszystkie wywołania
              </Button>
            )}
          </div>

          <h3 style={{ fontSize: 13 }}>Wyłączone operacje</h3>
          <DataTable
            rows={data.disabledOperationKeys.map((key) => ({ key }))}
            rowKey={(row) => row.key}
            empty={
              <p style={{ color: 'var(--ah-text-muted)', fontSize: 13 }}>
                Brak wyłączonych operacji.
              </p>
            }
            columns={[
              {
                key: 'op',
                header: 'Operacja',
                render: (row) => <span className="ah-mono">{row.key}</span>,
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) => (
                  <Button size="sm" onClick={() => emergency('ENABLE_OPERATION', row.key)}>
                    Włącz ponownie
                  </Button>
                ),
              },
            ]}
          />

          <h3 style={{ fontSize: 13, marginTop: 16 }}>Wyłączone konta dostawców</h3>
          <DataTable
            rows={data.disabledProviderAccountIds.map((id) => ({ id }))}
            rowKey={(row) => row.id}
            empty={
              <p style={{ color: 'var(--ah-text-muted)', fontSize: 13 }}>Brak wyłączonych kont.</p>
            }
            columns={[
              {
                key: 'id',
                header: 'Identyfikator',
                render: (row) => <span className="ah-mono">{row.id}</span>,
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) => (
                  <Button size="sm" onClick={() => emergency('ENABLE_PROVIDER_ACCOUNT', row.id)}>
                    Włącz ponownie
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}

      <ConfirmDialog
        open={stopConfirm}
        onOpenChange={setStopConfirm}
        title="Zatrzymać cały ruch wychodzący?"
        description="Każde wywołanie — REST, MCP i konsola testowa — natychmiast zwróci EMERGENCY_STOP. Akcja jest audytowana."
        confirmLabel="Zatrzymaj"
        destructive
        confirmPhrase="ZATRZYMAJ"
        onConfirm={() => emergency('STOP_ALL')}
      />
    </>
  );
}

/** Global settings form. Its initial state comes from the loaded settings. */
function GlobalSettingsForm(props: { settings: Settings; isOwner: boolean; onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState(() => ({
    hubDisplayName: props.settings.hubDisplayName,
    telemetryRetentionDays: String(props.settings.telemetryRetentionDays),
    globalRpmLimit: props.settings.globalRpmLimit ? String(props.settings.globalRpmLimit) : '',
    globalConcurrentLimit: props.settings.globalConcurrentLimit
      ? String(props.settings.globalConcurrentLimit)
      : '',
    developerTokenDefaultTtlDays: String(props.settings.developerTokenDefaultTtlDays),
  }));
  const [busy, setBusy] = useState(false);

  return (
    <Card
      title="Ustawienia globalne"
      description={props.isOwner ? undefined : 'Edycja wymaga roli OWNER.'}
    >
      <div className="ah-grid cols-2">
        <Field label="Nazwa Hub" hint="Widoczna w manifeście agenta i w pakietach integracyjnych.">
          {(id) => (
            <TextInput
              id={id}
              disabled={!props.isOwner}
              value={form.hubDisplayName}
              onChange={(event) => setForm({ ...form, hubDisplayName: event.target.value })}
            />
          )}
        </Field>
        <Field label="Retencja telemetrii (dni)">
          {(id) => (
            <TextInput
              id={id}
              type="number"
              disabled={!props.isOwner}
              value={form.telemetryRetentionDays}
              onChange={(event) => setForm({ ...form, telemetryRetentionDays: event.target.value })}
            />
          )}
        </Field>
        <Field
          label="Globalny limit RPM"
          hint="Puste = bez limitu. Awaryjny hamulec dla całego Hub."
        >
          {(id) => (
            <TextInput
              id={id}
              type="number"
              disabled={!props.isOwner}
              value={form.globalRpmLimit}
              onChange={(event) => setForm({ ...form, globalRpmLimit: event.target.value })}
            />
          )}
        </Field>
        <Field label="Globalny limit równoległości">
          {(id) => (
            <TextInput
              id={id}
              type="number"
              disabled={!props.isOwner}
              value={form.globalConcurrentLimit}
              onChange={(event) => setForm({ ...form, globalConcurrentLimit: event.target.value })}
            />
          )}
        </Field>
        <Field label="Domyślna ważność tokenu deweloperskiego (dni)">
          {(id) => (
            <TextInput
              id={id}
              type="number"
              disabled={!props.isOwner}
              value={form.developerTokenDefaultTtlDays}
              onChange={(event) =>
                setForm({ ...form, developerTokenDefaultTtlDays: event.target.value })
              }
            />
          )}
        </Field>
      </div>
      {props.isOwner && (
        <Button
          variant="primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await api.patch('/api/admin/settings', {
                hubDisplayName: form.hubDisplayName,
                telemetryRetentionDays: Number(form.telemetryRetentionDays),
                globalRpmLimit: form.globalRpmLimit ? Number(form.globalRpmLimit) : null,
                globalConcurrentLimit: form.globalConcurrentLimit
                  ? Number(form.globalConcurrentLimit)
                  : null,
                developerTokenDefaultTtlDays: Number(form.developerTokenDefaultTtlDays),
              });
              toast.success('Ustawienia zapisane');
              props.onSaved();
            } catch (error) {
              const described = describeError(error);
              toast.error(described.title, described.body);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Zapisuję…' : 'Zapisz'}
        </Button>
      )}
    </Card>
  );
}
